import { useEffect, useState } from "react";
import {
  XMarkIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  EyeIcon,
  EyeSlashIcon,
  TrashIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { buildKanbanColumns, genColumnId, type KanbanColumn } from "./columns";
import type { Status } from "@/types";

const DEFAULT_COLUMN_COLOR = "#6B7280";

interface KanbanColumnSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  columns: KanbanColumn[];
  statuses: Status[];
  onSave: (next: KanbanColumn[]) => void;
}

export function KanbanColumnSettings({ isOpen, onClose, columns, statuses, onSave }: KanbanColumnSettingsProps) {
  const [draft, setDraft] = useState<KanbanColumn[]>(columns);

  useEffect(() => {
    if (isOpen) setDraft(columns.slice().sort((a, b) => a.order - b.order));
  }, [isOpen, columns]);

  if (!isOpen) return null;

  const move = (index: number, dir: -1 | 1) => {
    const next = draft.slice();
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setDraft(next.map((c, i) => ({ ...c, order: i })));
  };

  const patch = (id: string, p: Partial<KanbanColumn>) =>
    setDraft(draft.map((c) => (c.id === id ? { ...c, ...p } : c)));

  const removeColumn = (id: string) => setDraft(draft.filter((c) => c.id !== id).map((c, i) => ({ ...c, order: i })));

  // Statuses that don't yet have a column — the only ones you can add (every column maps to one status).
  const usedStatusIds = new Set(draft.filter((c) => c.statusId).map((c) => c.statusId as string));
  const availableStatuses = statuses.filter((s) => !usedStatusIds.has(s.id));
  const canAddColumn = availableStatuses.length > 0;

  const addColumn = () => {
    const st = availableStatuses[0];
    if (!st) return;
    setDraft([
      ...draft,
      {
        id: genColumnId(),
        statusId: st.id,
        label: st.name,
        color: st.color || DEFAULT_COLUMN_COLOR,
        hidden: false,
        order: draft.length,
      },
    ]);
  };

  // When a column's status changes, auto-fill the label/color if the label is still a placeholder.
  const changeStatus = (id: string, statusId: string) => {
    const st = statuses.find((s) => s.id === statusId);
    setDraft(
      draft.map((c) => {
        if (c.id !== id) return c;
        const isPlaceholder = !c.label.trim() || c.label === "New column";
        return {
          ...c,
          statusId,
          label: st && isPlaceholder ? st.name : c.label,
          color: st && isPlaceholder ? st.color : c.color,
        };
      })
    );
  };

  const save = () => {
    // Every column must map to a status — drop any that somehow lack one.
    onSave(draft.filter((c) => c.statusId).map((c, i) => ({ ...c, order: i })));
    onClose();
  };

  const resetToStatuses = () => setDraft(buildKanbanColumns(statuses));

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-background-primary rounded-lg shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div>
            <h2 className="text-base font-semibold text-text-primary">Kanban columns</h2>
            <p className="text-xs text-text-tertiary mt-0.5">
              Add, reorder, rename, recolor or hide columns. Each column maps to one status.
            </p>
            <p className="text-xs text-text-tertiary mt-0.5">Default column sort: Start Month (asc)</p>
          </div>
          <button onClick={onClose} className="p-1 text-text-tertiary hover:text-text-primary rounded">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-4 space-y-2">
          {draft.map((col, index) => {
            // Statuses already mapped by another column — disabled to prevent duplicate columns
            const usedElsewhere = new Set(
              draft.filter((c) => c.id !== col.id && c.statusId).map((c) => c.statusId as string)
            );
            return (
              <div
                key={col.id}
                className={`flex items-center gap-2 p-2 rounded-md border border-gray-200 ${
                  col.hidden ? "bg-gray-50 opacity-70" : "bg-background-primary"
                }`}
              >
                <div className="flex flex-col">
                  <button
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                    className="p-0.5 text-text-tertiary hover:text-text-primary disabled:opacity-30"
                    title="Move up"
                  >
                    <ArrowUpIcon className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => move(index, 1)}
                    disabled={index === draft.length - 1}
                    className="p-0.5 text-text-tertiary hover:text-text-primary disabled:opacity-30"
                    title="Move down"
                  >
                    <ArrowDownIcon className="w-3.5 h-3.5" />
                  </button>
                </div>

                <label className="relative flex-shrink-0" title="Color">
                  <span
                    className="block w-5 h-5 rounded-full border border-gray-300 cursor-pointer"
                    style={{ backgroundColor: col.color }}
                  />
                  <input
                    type="color"
                    value={col.color}
                    onChange={(e) => patch(col.id, { color: e.target.value })}
                    className="absolute inset-0 opacity-0 cursor-pointer w-5 h-5"
                  />
                </label>

                <div className="flex-1 min-w-0 space-y-1">
                  <input
                    value={col.label}
                    onChange={(e) => patch(col.id, { label: e.target.value })}
                    placeholder="Column name"
                    className="w-full px-2 py-1 text-sm border border-gray-200 rounded outline-none focus:border-blue-400"
                  />
                  <select
                    value={col.statusId ?? ""}
                    onChange={(e) => changeStatus(col.id, e.target.value)}
                    className="w-full px-2 py-1 text-xs border border-gray-200 rounded outline-none focus:border-blue-400 bg-background-primary text-text-secondary"
                  >
                    {statuses.map((s) => (
                      <option key={s.id} value={s.id} disabled={usedElsewhere.has(s.id)}>
                        {s.name}
                        {usedElsewhere.has(s.id) ? " (in use)" : ""}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => patch(col.id, { hidden: !col.hidden })}
                    className="p-1.5 text-text-tertiary hover:text-text-primary hover:bg-gray-100 rounded"
                    title={col.hidden ? "Show column" : "Hide column"}
                  >
                    {col.hidden ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => removeColumn(col.id)}
                    className="p-1.5 text-text-tertiary hover:text-red-600 hover:bg-red-50 rounded"
                    title="Delete column"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}

          <button
            onClick={addColumn}
            disabled={!canAddColumn}
            title={canAddColumn ? undefined : "Every status already has a column. Create a new status under Settings → Status first."}
            className="w-full flex items-center justify-center gap-1.5 py-2 text-sm font-medium text-blue-600 border border-dashed border-blue-300 rounded-md hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          >
            <PlusIcon className="w-4 h-4" />
            Add column
          </button>
          {!canAddColumn && (
            <p className="text-[11px] text-text-tertiary text-center">
              Every status already has a column. Add a status under Settings → Status to create more.
            </p>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200">
          <button onClick={resetToStatuses} className="text-sm text-text-secondary hover:text-text-primary">
            Reset to statuses
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-gray-100 rounded-md"
            >
              Cancel
            </button>
            <button
              onClick={save}
              className="px-3 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
